import { useState, type ComponentProps, type ReactNode } from "react"
import { useLocalSessionId, useParticipantIds, useParticipantProperty, usePermissions, useWaitingParticipants } from "./callTransport"
import { Hand, Mail, Mic, MicOff, MoreHorizontal, Pin, PinOff, UserX, Video, VideoOff } from "lucide-react"
import { Button } from "components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip"
import { identityKeyFromDailyUserId } from "./BreakoutPanel"
import { AvatarBubble } from "../shared/AvatarBubble"
import type { CallSurfaceWaitRoom } from "../types"
import { useHostModeration, type MeetingRemovalScope } from "./useHostModeration"
import { useRaisedHandsContext } from "./useRaisedHands"
import { MobileWaitingRoomHostPanel } from "./MobileWaitingRoomHostPanel"
import { WaitingRoomHostPanel } from "./WaitingRoomHostPanel"

type PersonRowProps = {
  sessionId: string
  isLocal: boolean
  isPinned: boolean
  onTogglePin?: (sessionId: string) => void
  onMessage?: (recipientIdentityKey: string, name: string) => void
  moderation: ReturnType<typeof useHostModeration>
}

/** An icon button with a dark tooltip naming the action; the label doubles as the accessible name. */
function IconAction({ label, children, ...props }: { label: string; children: ReactNode } & Omit<ComponentProps<typeof Button>, "children" | "aria-label">) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" size="icon" variant="ghost" aria-label={label} {...props}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" className="border-white/10 bg-[#0f1419] text-white shadow-xl">{label}</TooltipContent>
    </Tooltip>
  )
}

function PersonRow({ sessionId, isLocal, isPinned, onTogglePin, onMessage, moderation }: PersonRowProps) {
  const userName = useParticipantProperty(sessionId, "user_name")
  const dailyUserId = useParticipantProperty(sessionId, "user_id")
  const identityKey = identityKeyFromDailyUserId(dailyUserId)
  const isOwner = useParticipantProperty(sessionId, "owner")
  const audioOn = useParticipantProperty(sessionId, "audio")
  const videoOn = useParticipantProperty(sessionId, "video")
  const [confirmRemove, setConfirmRemove] = useState(false)
  const hands = useRaisedHandsContext()
  const handPosition = hands.positionOf(sessionId)
  const name = (userName as string | undefined)?.trim() || "Guest"
  const showModeration = moderation.canModerate && !isLocal
  const canLowerThisHand = handPosition !== null && (isLocal || hands.canLowerOthers)
  const canMessage = Boolean(onMessage && !isLocal && identityKey)
  // Only Message stays beside the name; pin, hand, and host actions live in a menu so the name
  // keeps the width of a narrow panel.
  const hasMoreActions = Boolean(onTogglePin) || canLowerThisHand || showModeration
  const iconButton = "h-7 w-7 min-h-0 shrink-0 rounded-full p-0 text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-35"
  const menuItem = "min-h-9 gap-2.5 rounded-lg px-2.5 py-2 text-sm text-white/90 after:hidden focus:bg-white/10 data-[highlighted]:bg-white/10 data-[highlighted]:text-white"

  return (
    <li data-testid={`call-person-${sessionId}`} data-pinned={isPinned ? "true" : undefined} className="rounded-lg px-2 py-1.5 hover:bg-white/5">
      <div className="flex items-center gap-2">
        <AvatarBubble name={name} seed={sessionId} className="h-8 w-8 shrink-0 text-xs" />
        {/* The name truncates; the badges never shrink, so a long name or email cannot clip "Host" or the hand number. */}
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm text-white">
          <span data-testid={`call-person-name-${sessionId}`} title={name} className="min-w-0 truncate">{name}{isLocal ? " (you)" : ""}</span>
          {isOwner && <span className="shrink-0 rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/80">Host</span>}
          {isPinned && <Pin aria-label="Pinned" className="h-3.5 w-3.5 shrink-0 text-sky-200" />}
          {handPosition !== null && (
            <span data-testid={`call-person-hand-${sessionId}`} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#f6a35c]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#f6a35c]">
              <Hand className="h-3 w-3" aria-hidden="true" />
              {handPosition}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-white/60">
          {audioOn ? <Mic aria-label="Microphone on" className="h-3.5 w-3.5" /> : <MicOff aria-label="Microphone off" className="h-3.5 w-3.5" />}
          {videoOn ? <Video aria-label="Camera on" className="h-3.5 w-3.5" /> : <VideoOff aria-label="Camera off" className="h-3.5 w-3.5" />}
        </span>
        {canMessage && (
          <IconAction label={`Message ${name}`} data-testid={`call-person-message-${sessionId}`} onClick={() => onMessage?.(identityKey!, name)} className={iconButton}>
            <Mail className="h-4 w-4" />
          </IconAction>
        )}
        {hasMoreActions && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={`More actions for ${name}`}
                title={`More actions for ${name}`}
                data-testid={`call-person-actions-${sessionId}`}
                className={`${iconButton} data-[state=open]:bg-white/15 data-[state=open]:text-white`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            {/* The call surface is a fixed full-screen layer, so the menu portal sits above it like the Polls menu. */}
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              data-testid={`call-person-actions-menu-${sessionId}`}
              // The shared menu fades in over a second (a global rule, hence !); an in-call action menu should appear at once.
              className="z-[250] w-56 ![animation-duration:120ms] rounded-xl border border-white/10 bg-[#1c2530] p-1 text-white shadow-2xl backdrop-blur-none"
            >
              <DropdownMenuLabel className="truncate px-2.5 pb-1 pt-1.5 text-[11px] font-semibold normal-case tracking-normal text-white/50">{name}{isLocal ? " (you)" : ""}</DropdownMenuLabel>
              {onTogglePin && (
                <DropdownMenuItem className={menuItem} onSelect={() => onTogglePin(sessionId)}>
                  {isPinned ? <PinOff className="h-4 w-4 text-white/60" aria-hidden="true" /> : <Pin className="h-4 w-4 text-white/60" aria-hidden="true" />}
                  {isPinned ? "Unpin from stage" : "Pin to stage"}
                </DropdownMenuItem>
              )}
              {canLowerThisHand && (
                <DropdownMenuItem className={menuItem} onSelect={() => hands.lowerParticipantHand(sessionId)}>
                  <Hand className="h-4 w-4 text-white/60" aria-hidden="true" />
                  {isLocal ? "Lower your hand" : "Lower hand"}
                </DropdownMenuItem>
              )}
              {showModeration && (
                <>
                  {(onTogglePin || canLowerThisHand) && <DropdownMenuSeparator className="my-1 bg-white/10" />}
                  <DropdownMenuItem className={menuItem} disabled={!audioOn} onSelect={() => moderation.muteParticipant(sessionId)}>
                    <MicOff className="h-4 w-4 text-white/60" aria-hidden="true" />
                    Mute
                  </DropdownMenuItem>
                  <DropdownMenuItem className={menuItem} disabled={!videoOn} onSelect={() => moderation.stopParticipantVideo(sessionId)}>
                    <VideoOff className="h-4 w-4 text-white/60" aria-hidden="true" />
                    Turn off camera
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 bg-white/10" />
                  <DropdownMenuItem className={`${menuItem} text-red-300 data-[highlighted]:bg-red-500/15 data-[highlighted]:text-red-200`} onSelect={() => setConfirmRemove(true)}>
                    <UserX className="h-4 w-4" aria-hidden="true" />
                    Remove from meeting
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {showModeration && confirmRemove && (
        <span data-testid={`call-person-remove-confirm-${sessionId}`} className="mt-1.5 flex w-full flex-wrap items-center justify-end gap-2 text-xs text-white/80">
          <span>{moderation.removesAllMeetingDevices
            ? `Remove ${name} from this meeting on all their devices? They will need host readmission to rejoin. Their account will stay signed in.`
            : `Remove ${name} from the meeting?`}</span>
          <Button type="button" size="auto" disabled={Boolean(moderation.removingId) || moderation.retryingRemoval} onClick={() => {
            void moderation.removeParticipant(sessionId).then(removed => { if (removed) setConfirmRemove(false) })
          }} className="h-7 bg-red-500 px-2.5 text-xs text-white hover:bg-red-400">
            Remove
          </Button>
          <Button type="button" size="auto" variant="secondary" disabled={Boolean(moderation.removingId) || moderation.retryingRemoval} onClick={() => setConfirmRemove(false)} className="h-7 px-2.5 text-xs">
            Cancel
          </Button>
        </span>
      )}
    </li>
  )
}

type PeoplePanelProps = {
  mobileMeetingId?: string
  mobileRoomName?: string
  onNativeEject?: MeetingRemovalScope["onNativeEject"]
  waitRoom: CallSurfaceWaitRoom
  waitingRoomEnabled: boolean
  pinnedId?: string | null
  onTogglePin?: (sessionId: string) => void
  onAdmitted?: (participant: { id: string; name?: string }) => void
  /** Direct messages: a mail icon per person and an envelope with the unread count; absent when messaging is off. */
  messaging?: {
    unreadCount: number
    onOpen: () => void
    onMessage: (recipientIdentityKey: string, name: string) => void
  }
}

/**
 * Role-aware participant list. Everyone can pin a participant to the stage; hosts also get
 * waiting-room admission, per-person mute / camera-off / remove, and Mute all.
 */
export function PeoplePanel({ mobileMeetingId, mobileRoomName, onNativeEject, waitRoom, waitingRoomEnabled, pinnedId = null, onTogglePin, onAdmitted, messaging }: PeoplePanelProps) {
  const localSessionId = useLocalSessionId()
  const participantIds = useParticipantIds({ sort: "joined_at" })
  const { canAdminParticipants } = usePermissions()
  const { waitingParticipants } = useWaitingParticipants()
  const moderation = useHostModeration({ meetingId: mobileMeetingId, roomName: mobileRoomName, onNativeEject })
  const hands = useRaisedHandsContext()
  const showLowerAll = hands.raisedIds.length > 0 && (hands.canLowerOthers || (hands.localRaised && hands.raisedIds.length === 1))

  return (
    <TooltipProvider delayDuration={300}>
    <div data-testid="call-people-panel" className="space-y-5">
      {moderation.removalNotice && <p role="status" className="text-sm text-amber-200">{moderation.removalNotice}</p>}
      {moderation.canRetryRemoval && <Button type="button" variant="secondary" disabled={Boolean(moderation.removingId) || moderation.retryingRemoval} onClick={() => void moderation.retryRemoval()}>{moderation.retryingRemoval ? 'Retrying disconnection…' : 'Retry disconnection'}</Button>}
      {moderation.removalError && <p role="alert" className="text-sm text-red-200">{moderation.removalError}</p>}
      {mobileMeetingId && canAdminParticipants && <MobileWaitingRoomHostPanel meetingId={mobileMeetingId} />}
      {(waitingRoomEnabled || waitingParticipants.length > 0) && canAdminParticipants && (
        <WaitingRoomHostPanel waitRoom={waitRoom} onAdmitted={onAdmitted} showEmptyState={!mobileMeetingId} />
      )}
      <section>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-white">In this meeting ({participantIds.length})</h3>
          <span className="flex items-center gap-1.5">
            {messaging && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" size="auto" variant="secondary" aria-label={messaging.unreadCount > 0 ? `Direct messages, ${messaging.unreadCount} unread` : "Direct messages"} onClick={messaging.onOpen} data-testid="call-people-messages" className="relative h-7 gap-1.5 px-2.5 text-xs">
                    <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                    Messages
                    {messaging.unreadCount > 0 && (
                      <span data-testid="call-people-messages-unread" className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d64545] px-1 text-[10px] font-bold text-white">{messaging.unreadCount}</span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="border-white/10 bg-[#0f1419] text-white shadow-xl">Open your direct messages</TooltipContent>
              </Tooltip>
            )}
            {showLowerAll && (
              <Button type="button" size="auto" variant="secondary" onClick={hands.lowerAllHands} className="h-7 px-2.5 text-xs">
                Lower all hands
              </Button>
            )}
            {moderation.canModerate && moderation.hasRemoteParticipants && (
              <Button type="button" size="auto" variant="secondary" onClick={moderation.muteAll} className="h-7 px-2.5 text-xs">
                Mute all
              </Button>
            )}
          </span>
        </div>
        <ul className="space-y-1">
          {participantIds.map((id) => (
            <PersonRow
              key={id}
              sessionId={id}
              isLocal={id === localSessionId}
              isPinned={id === pinnedId}
              onTogglePin={onTogglePin}
              onMessage={messaging?.onMessage}
              moderation={moderation}
            />
          ))}
        </ul>
      </section>
    </div>
    </TooltipProvider>
  )
}
